import { useState, useEffect } from "react";
import { toast } from "sonner";
import { http, formatDetail, auDateTime } from "./api";
import { usePortalUser } from "./PortalLayout";

const ACTION_LABELS = {
  file_downloaded: "Downloaded",
  licence_acknowledged: "Licence acknowledged",
  download_link_issued: "Download link issued",
  evidence_submitted: "Evidence submitted",
  password_changed: "Password changed",
  message_sent: "Message sent",
};

export default function PortalAccount() {
  const account = usePortalUser();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    http.get("/portal/client/account/activity")
      .then(({ data }) => setActivity(data))
      .catch(() => setActivity([]));
  }, []);

  const change = async (e) => {
    e.preventDefault();
    if (next.length < 10) return toast.error("New password must be at least 10 characters.");
    setBusy(true);
    try {
      await http.post("/portal/client/account/password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      setCurrent(""); setNext("");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="portal-account" className="max-w-2xl">
      <h1 className="font-serif text-3xl md:text-4xl mb-8">Your account</h1>
      <div className="border border-line bg-surface1 p-6 md:p-8 mb-8 space-y-3">
        <p className="text-sm"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre block mb-1">Name</span>{account.name}</p>
        <p className="text-sm"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre block mb-1">Email</span>{account.email}</p>
        <p className="text-sm"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre block mb-1">Organisation</span>{account.organisation}</p>
        <p className="text-sm text-stone2"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre block mb-1">Multi-factor authentication</span>{account.mfa_note}</p>
      </div>
      <form onSubmit={change} className="border border-line bg-surface1 p-6 md:p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-5">Change password</p>
        <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2">Current password</label>
        <input type="password" data-testid="account-current-pw" className="field-dark w-full px-4 py-3 text-sm mb-5" value={current} onChange={(e) => setCurrent(e.target.value)} />
        <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2">New password (min 10 characters)</label>
        <input type="password" data-testid="account-new-pw" className="field-dark w-full px-4 py-3 text-sm mb-6" value={next} onChange={(e) => setNext(e.target.value)} />
        <button type="submit" disabled={busy} data-testid="account-change-pw-btn" className="font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60">
          {busy ? "Saving…" : "Update password"}
        </button>
      </form>
      <div className="border border-line bg-surface1 p-6 md:p-8 mt-8" data-testid="account-activity">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-5">Your recent activity</p>
        {activity === null ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>
        ) : activity.length === 0 ? (
          <p className="text-sm text-stone2">No activity recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {activity.map((a, i) => (
              <div key={i} data-testid={`activity-row-${i}`} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-line/40 pb-2">
                <time className="font-mono text-[10px] text-stone2 w-32">{auDateTime(a.created_at)}</time>
                <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ochre">{ACTION_LABELS[a.action] || a.action}</span>
                {a.detail && <span className="text-sm text-stone2">{a.detail}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
